import {
    useCallback,
    useEffect,
    useRef,
    useState,
} from "react";

import { strings } from "@/constants";
import { JobStatusEnum } from "@/enums";
import { useBenchmarkStatus, useGenerateBenchmark } from "@/hooks/benchmarks";
import type {
    BenchmarkRecommendedJobType,
    BenchmarkResultPayloadType,
    BenchmarkStatusResponseType,
    GenerateBenchmarkRequestType,
} from "@/types";
import {
    clearBenchmarkResultCacheHandler,
    loadBenchmarkResultCacheHandler,
    saveBenchmarkResultCacheHandler,
} from "@/utils";

interface UsePollingResultInterface {
    industry: string,
    jobDescription: string,
    jobId: string,
    jobTitle: string,
    recommendedJobs: BenchmarkRecommendedJobType[],
    resultPayload: BenchmarkResultPayloadType,
    seniority: string,
}

export interface UseBenchmarkPollingReturnInterface {
    errorMessage: string,
    onPollExisting: (input: { jobId: string }) => Promise<void>,
    onProcess: (input: GenerateBenchmarkRequestType) => Promise<void>,
    onReset: () => void,
    progress: number,
    result: UsePollingResultInterface | null,
    status: "error" | "idle" | "loading" | "success",
}

interface UseBenchmarkPollingOptionsInterface {
    enableCache?: boolean,
}

export const useBenchmarkPolling = ({ enableCache = false }: UseBenchmarkPollingOptionsInterface = {}): UseBenchmarkPollingReturnInterface => {
    const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);

    const isCompletedRef = useRef(false);

    const jobIdRef = useRef<string | null>(null);

    const cachedInitial = enableCache ? loadBenchmarkResultCacheHandler() : null;

    const [status, setStatus] = useState<"error" | "idle" | "loading" | "success">(cachedInitial ? "success" : "idle");

    const [progress, setProgress] = useState(cachedInitial ? 100 : 0);

    const [errorMessage, setErrorMessage] = useState("");

    const [result, setResult] = useState<UsePollingResultInterface | null>(cachedInitial);

    const { onRun: runGenerateBenchmarkHandler } = useGenerateBenchmark();

    const { onRun: runBenchmarkStatusHandler } = useBenchmarkStatus();

    const pollingIntervalMs = 3000;

    const stopPollingHandler = useCallback(
        () => {
            if (pollingRef.current) {
                clearInterval(pollingRef.current);

                pollingRef.current = null;
            }
        },
        [],
    );

    const handleStatusResponseHandler = useCallback(
        (
            statusResponse: BenchmarkStatusResponseType,
            request: GenerateBenchmarkRequestType,
            jobId: string,
        ): boolean => {
            const {
                industry,
                jobDescription,
                jobTitle,
                seniority,
            } = request;

            const {
                displayProgress,
                errorMessage: statusErrorMessage,
                progress,
                result,
                status,
            } = statusResponse;

            if (isCompletedRef.current) return true;

            setProgress(displayProgress ?? progress);

            if (result) {
                isCompletedRef.current = true;

                stopPollingHandler();

                setProgress(100);

                setStatus("success");

                const cachedResult: UsePollingResultInterface = {
                    industry: industry,
                    jobDescription: jobDescription,
                    jobId,
                    jobTitle: jobTitle,
                    recommendedJobs: result.recommendedJobs ?? [],
                    resultPayload: result,
                    seniority: String(seniority),
                };

                setResult(cachedResult);

                if (enableCache) saveBenchmarkResultCacheHandler(cachedResult);

                return true;
            }

            if (status === JobStatusEnum.FAILED) {
                stopPollingHandler();

                setStatus("error");

                setErrorMessage(statusErrorMessage || strings.errors.tryAgain);

                return true;
            }

            return false;
        },
        [stopPollingHandler, enableCache],
    );

    const beginPollingHandler = useCallback(
        (
            request: GenerateBenchmarkRequestType,
            jobId: string,
        ) => {
            const tickHandler = async () => {
                if (!jobIdRef.current || isCompletedRef.current) {
                    stopPollingHandler();

                    return;
                }

                try {
                    const statusResponse = await runBenchmarkStatusHandler(jobIdRef.current);

                    handleStatusResponseHandler(
                        statusResponse,
                        request,
                        jobId,
                    );
                } catch (error) {
                    stopPollingHandler();

                    setStatus("error");

                    setErrorMessage(error instanceof Error ? error.message : strings.errors.tryAgain);
                }
            };

            return tickHandler;
        },
        [
            runBenchmarkStatusHandler,
            stopPollingHandler,
            handleStatusResponseHandler,
        ],
    );

    const processHandler = useCallback(
        async (request: GenerateBenchmarkRequestType) => {
            jobIdRef.current = null;

            isCompletedRef.current = false;

            setStatus("loading");

            setErrorMessage("");

            setProgress(0);

            setResult(null);

            if (enableCache) clearBenchmarkResultCacheHandler();

            try {
                const generateResponse = await runGenerateBenchmarkHandler(request);

                if (!generateResponse.jobId) {
                    setStatus("error");

                    setErrorMessage(strings.errors.tryAgain);

                    return;
                }

                jobIdRef.current = generateResponse.jobId;

                const tickHandler = beginPollingHandler(
                    request,
                    generateResponse.jobId,
                );

                await tickHandler();

                if (jobIdRef.current && !isCompletedRef.current) {
                    pollingRef.current = setInterval(
                        tickHandler,
                        pollingIntervalMs,
                    );
                }
            } catch (err) {
                setStatus("error");

                setErrorMessage(err instanceof Error ? err.message : strings.errors.tryAgain);
            }
        },
        [
            runGenerateBenchmarkHandler,
            beginPollingHandler,
            enableCache,
        ],
    );

    const pollExistingHandler = useCallback(
        async ({ jobId }: { jobId: string }) => {
            jobIdRef.current = jobId;

            isCompletedRef.current = false;

            setStatus("loading");

            setErrorMessage("");

            setProgress(0);

            const placeholderRequest: GenerateBenchmarkRequestType = {
                industry: "",
                jobDescription: "",
                jobTitle: "",
                seniority: "",
            };

            const tickHandler = beginPollingHandler(
                placeholderRequest,
                jobId,
            );

            await tickHandler();

            if (jobIdRef.current && !isCompletedRef.current) {
                pollingRef.current = setInterval(
                    tickHandler,
                    pollingIntervalMs,
                );
            }
        },
        [beginPollingHandler],
    );

    const resetHandler = useCallback(
        () => {
            stopPollingHandler();

            jobIdRef.current = null;

            isCompletedRef.current = false;

            setStatus("idle");

            setErrorMessage("");

            setProgress(0);

            setResult(null);

            if (enableCache) clearBenchmarkResultCacheHandler();
        },
        [stopPollingHandler, enableCache],
    );

    useEffect(
        () => () => stopPollingHandler(),
        [stopPollingHandler],
    );

    return {
        errorMessage,
        onPollExisting: pollExistingHandler,
        onProcess: processHandler,
        onReset: resetHandler,
        progress,
        result,
        status,
    };
};
